/**
 * Pino-based structured logger for frontend.
 * Provides consistent logging format matching backend.
 */

import pino from "pino";

// Determine log level based on environment
const LOG_LEVEL =
  import.meta.env.VITE_LOG_LEVEL || (import.meta.env.DEV ? "debug" : "info");

// Create Pino logger instance
const pinoLogger = pino({
  name: "responsum-frontend",
  level: LOG_LEVEL,
  browser: {
    asObject: true,
    serialize: true,
    // Custom transmit for development (sends to console nicely)
    transmit: {
      level: "info",
      send: (level, logEvent) => {
        const { ts, messages, bindings } = logEvent;

        // Extract context from bindings
        const context = bindings.reduce(
          (acc, binding) => {
            return { ...acc, ...binding };
          },
          {} as Record<string, unknown>,
        );

        // Format for console
        const timestamp = new Date(ts).toISOString();
        const levelNum =
          typeof level === "string" ? parseInt(level, 10) : level;
        const levelName =
          (pino.levels.labels as Record<number, string>)[levelNum] || "unknown";
        const message = messages[0];

        if (import.meta.env.DEV) {
          // Development: pretty print
          const logFn =
            levelNum >= 50
              ? console.error
              : levelNum >= 40
                ? console.warn
                : console.log;

          logFn(
            `[${timestamp}] ${levelName.toUpperCase()}: ${message}`,
            context,
          );
        }
      },
    },
  },
  // Base context for all logs
  base: {
    app: "responsum-frontend",
    env: import.meta.env.MODE,
  },
});

// Export typed logger interface for convenience
export interface Logger {
  debug(msg: string, context?: Record<string, unknown>): void;
  info(msg: string, context?: Record<string, unknown>): void;
  warn(msg: string, context?: Record<string, unknown>): void;
  error(msg: string, context?: Record<string, unknown>): void;
}

// Wrapper that matches our Logger interface (message first, context second)
export const logger: Logger = {
  debug(msg: string, context?: Record<string, unknown>): void {
    if (context) {
      pinoLogger.debug(context, msg);
    } else {
      pinoLogger.debug(msg);
    }
  },
  info(msg: string, context?: Record<string, unknown>): void {
    if (context) {
      pinoLogger.info(context, msg);
    } else {
      pinoLogger.info(msg);
    }
  },
  warn(msg: string, context?: Record<string, unknown>): void {
    if (context) {
      pinoLogger.warn(context, msg);
    } else {
      pinoLogger.warn(msg);
    }
  },
  error(msg: string, context?: Record<string, unknown>): void {
    if (context) {
      pinoLogger.error(context, msg);
    } else {
      pinoLogger.error(msg);
    }
  },
};

// Create child logger with additional context
export function createChildLogger(
  context: Record<string, unknown>,
): pino.Logger {
  return pinoLogger.child(context);
}

// Utility to log performance metrics
export function logPerformance(
  name: string,
  duration: number,
  metadata?: Record<string, unknown>,
): void {
  logger.info("performance_metric", {
    metric_name: name,
    duration_ms: duration,
    ...metadata,
  });
}

// Utility to log user actions
export function logUserAction(
  action: string,
  details?: Record<string, unknown>,
): void {
  logger.info("user_action", {
    action,
    ...details,
  });
}

// Utility to log WebSocket events
export function logWebSocketEvent(
  event: string,
  details?: Record<string, unknown>,
): void {
  logger.debug("websocket_event", {
    event,
    ...details,
  });
}
